import type { TableOut, BaseRegisterTableInput, BaseRegisterSelectAllInput, BaseRegisterSelectAllGroupInput, StatisticalCode, CostCenter, Work, BaseRegisterSelectionInput, BaseRegisterEmploymentSelectionInput, BaseRegisterEmploymentSelectionOutput, SalaryMappingAvatar, VerifyOrganizationalGroupingResponse } from '@sysarb/sysarb-types-v2/services/v1/base-registry';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type { OrganisationNode } from '@sysarb/sysarb-types/services/v1/organisation-node';
import type { LegalEntity } from '@sysarb/sysarb-types/general/types';
import type { useResgate, Collection, Model } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';

export class BaseRegister {
    private readonly rest = new RestManager(`https://${getApiHost()}/base-registry-service`);

    public abort(): void {
        this.rest.abort();
    }

    public async getManyRest(input: BaseRegisterTableInput): Promise<TableOut> {
        const { instance } = useLoginStore();
        return this.rest.post(input, `v1/${instance}/base-register/filter`);
    }

    public async getIdsToSelect(input: BaseRegisterSelectAllInput): Promise<string[]> {
        const { instance } = useLoginStore();
        return this.rest.post(input, `v1/${instance}/base-register/select`);
    }

    public async getGroupsToSelect(input: BaseRegisterSelectAllGroupInput): Promise<string[]> {
        const { instance } = useLoginStore();
        return this.rest.post(input, `v1/${instance}/base-register/select/groups`);
    }

    public async getOrganizationDetails(resgate: ReturnType<typeof useResgate>, id: string): Promise<Model<OrganisationNode> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.model<OrganisationNode>(`v1.${instanceTopic}.organisation-node.${id}`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    public async getStatisticalCodes(resgate: ReturnType<typeof useResgate>): Promise<Collection<StatisticalCode> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.collection<StatisticalCode>(`v1.${instanceTopic}.statisticalcodes`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    public async getCostCenters(resgate: ReturnType<typeof useResgate>): Promise<Collection<CostCenter> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.collection<CostCenter>(`v1.${instanceTopic}.costcenters`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    public async getLegalEntities(resgate: ReturnType<typeof useResgate>): Promise<Collection<LegalEntity> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.collection<LegalEntity>(`v1.${instanceTopic}.legalentities`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    public async verifyOrganizationalGrouping(resgate: ReturnType<typeof useResgate>): Promise<(VerifyOrganizationalGroupingResponse) | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.call<VerifyOrganizationalGroupingResponse>(`v1.${instanceTopic}.legalentities`, 'verify-organizational-grouping', {}).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    public async getLegalEntity(resgate: ReturnType<typeof useResgate>, id: string): Promise<Model<LegalEntity> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.model<LegalEntity>(`v1.${instanceTopic}.legalentity.${id}`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    public async getWorks(resgate: ReturnType<typeof useResgate>): Promise<Collection<Work> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.collection<Work>(`v1.${instanceTopic}.works`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }

    /**
     * Used to get all matching employment/user ids for the given employee ids on the given filter
     */
    public async getMatchingIds(input: BaseRegisterSelectionInput): Promise<string[]> {
        const { instance } = useLoginStore();
        return this.rest.post(input, `v1/${instance}/base-register/selection/matching-ids`);
    }

    public async getSelectionEmploymentInfo(input: BaseRegisterEmploymentSelectionInput): Promise<BaseRegisterEmploymentSelectionOutput> {
        const { instance } = useLoginStore();
        return this.rest.post(input, `v1/${instance}/base-register/selection/employments`);
    }

    public async getSalaryMappingAvatars(resgate: ReturnType<typeof useResgate>): Promise<Collection<SalaryMappingAvatar> | null> {
        const { instanceTopic } = useLoginStore();

        return resgate.collection<SalaryMappingAvatar>(`v1.${instanceTopic}.salary-mappings.avatars`).then(res => {
            if (res?.success === false) {
                return null;
            }

            return res.data;
        });
    }
}
